import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { BookOpen, Clock, User, ArrowRight } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EducationPage = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showFullContent, setShowFullContent] = useState({});

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setBlogPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const categories = ["All", "Loan Programs", "VA Loans", "Mortgage Basics", "Market Trends", "First-Time Buyers", "Refinancing", "Alternative Programs"];
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredPosts = selectedCategory === "All" 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const featuredPosts = blogPosts.filter(post => post.featured);

  const toggleContent = (postId) => {
    setShowFullContent(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0b2c4a] to-[#1a4b73] text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
              <BookOpen className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Mortgage & Real Estate Education
            </h1>
            <p className="text-xl text-blue-100">
              Stay informed with our comprehensive guides, market insights, and expert advice 
              on mortgages, real estate, and homeownership.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Featured Articles</h2>
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0b2c4a] mx-auto mb-4"></div>
              <p className="text-gray-600">Loading articles...</p>
            </div>
          ) : featuredPosts.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No featured articles</h3>
              <p className="text-gray-600">Check back soon for featured content.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {featuredPosts.slice(0, 3).map((post) => (
              <Card key={post.id} className="hover:shadow-lg transition-shadow duration-200 border-none bg-white">
                <CardHeader className="pb-4">
                  <Badge className="w-fit bg-[#0b2c4a]/10 text-[#0b2c4a] hover:bg-[#0b2c4a]/20 mb-2">
                    {post.category}
                  </Badge>
                  <CardTitle className="text-lg text-gray-900 line-clamp-2">
                    {post.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <User className="h-3 w-3" />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="h-3 w-3" />
                        <span>{post.read_time}</span>
                      </div>
                    </div>
                    <span>{new Date(post.created_at).toLocaleDateString()}</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    className="w-full text-[#0b2c4a] hover:bg-[#0b2c4a]/10 p-0 h-auto"
                    onClick={() => toggleContent(post.id)}
                  >
                    {showFullContent[post.id] ? "Show Less" : "Read More"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  {showFullContent[post.id] && (
                    <div className="mt-4 pt-4 border-t text-sm text-gray-700 leading-relaxed">
                      {post.content}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            </div>
          )}
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-slate-50 border-b">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                size="sm"
                className={selectedCategory === category 
                  ? "bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white" 
                  : "border-[#0b2c4a] text-[#0b2c4a] hover:bg-[#0b2c4a] hover:text-white"
                }
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* All Articles */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {selectedCategory === "All" ? "All Articles" : selectedCategory}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Browse our comprehensive collection of mortgage and real estate articles, 
              guides, and market insights.
            </p>
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0b2c4a] mx-auto mb-4"></div>
              <p className="text-gray-600">Loading articles...</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No articles found</h3>
              <p className="text-gray-600">Check back soon for articles in the {selectedCategory} category.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <Card key={post.id} className="hover:shadow-lg transition-shadow duration-200 border-none bg-white">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className="bg-[#0b2c4a]/10 text-[#0b2c4a] hover:bg-[#0b2c4a]/20">
                        {post.category}
                      </Badge>
                      {post.featured && (
                        <Badge className="bg-yellow-100 text-yellow-800">
                          Featured
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-lg text-gray-900 line-clamp-2">
                      {post.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <User className="h-3 w-3" />
                          <span>{post.author}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock className="h-3 w-3" />
                          <span>{post.read_time}</span>
                        </div>
                      </div>
                      <span>{new Date(post.created_at).toLocaleDateString()}</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      className="w-full text-[#0b2c4a] hover:bg-[#0b2c4a]/10 p-0 h-auto"
                      onClick={() => toggleContent(post.id)}
                    >
                      {showFullContent[post.id] ? "Show Less" : "Read More"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    {showFullContent[post.id] && (
                      <div className="mt-4 pt-4 border-t text-sm text-gray-700 leading-relaxed">
                        {post.content}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Stay Updated with Market Insights
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Get the latest mortgage rates, market trends, and homebuying tips 
            delivered directly to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
              onClick={() => window.location.href = 'tel:(720) 650-7988'}
            >
              Contact Us for Updates
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-[#0b2c4a] text-[#0b2c4a] hover:bg-[#0b2c4a] hover:text-white"
              asChild
            >
              <a href="/calculators">Try Our Calculators</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EducationPage;